import {firestore} from '../config/firebase';
import firestoreModule from '@react-native-firebase/firestore';

/**
 * Generate a unique mutual aid code
 * Format: 6-character alphanumeric (e.g., "ABC123")
 */
export function generateMutualAidCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Exclude confusing chars (0, O, I, 1)
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Ensure account has a mutual aid code (generate if missing)
 */
export async function ensureMutualAidCode(accountId: string): Promise<string> {
  try {
    const accountRef = firestore().collection('accounts').doc(accountId);
    const accountDoc = await accountRef.get();
    
    if (!accountDoc.exists) {
      throw new Error('Account not found');
    }
    
    const accountData = accountDoc.data();
    
    // If code already exists, return it
    if (accountData?.mutualAidCode) {
      return accountData.mutualAidCode;
    }
    
    // Generate new code and ensure uniqueness
    let code = generateMutualAidCode();
    let attempts = 0;
    const maxAttempts = 10;
    
    while (attempts < maxAttempts) {
      // Check if code is already in use
      const existingAccount = await firestore()
        .collection('accounts')
        .where('mutualAidCode', '==', code)
        .limit(1)
        .get();
      
      if (existingAccount.empty) {
        // Code is unique, save it
        await accountRef.update({
          mutualAidCode: code,
        });
        return code;
      }
      
      // Code exists, generate new one
      code = generateMutualAidCode();
      attempts++;
    }
    
    throw new Error('Failed to generate unique mutual aid code');
  } catch (error) {
    console.error('Error ensuring mutual aid code:', error);
    throw error;
  }
}

/**
 * Search for account by mutual aid code
 */
export async function searchAccountByCode(code: string): Promise<{
  id: string;
  name: string;
  municipality: string;
} | null> {
  try {
    const codeUpper = code.toUpperCase().trim();
    
    const snapshot = await firestore()
      .collection('accounts')
      .where('mutualAidCode', '==', codeUpper)
      .limit(1)
      .get();
    
    if (snapshot.empty) {
      return null;
    }
    
    const accountDoc = snapshot.docs[0];
    const accountData = accountDoc.data();
    
    return {
      id: accountDoc.id,
      name: accountData.name || '',
      municipality: accountData.municipality || '',
    };
  } catch (error) {
    console.error('Error searching account by code:', error);
    throw error;
  }
}

/**
 * Add a mutual aid partner to your account
 */
export async function addMutualAidPartner(
  yourAccountId: string,
  partnerAccountId: string,
): Promise<void> {
  try {
    // Prevent adding yourself
    if (yourAccountId === partnerAccountId) {
      throw new Error('Cannot add your own account as a partner');
    }
    
    const accountRef = firestore().collection('accounts').doc(yourAccountId);
    const accountDoc = await accountRef.get();
    
    if (!accountDoc.exists) {
      throw new Error('Your account not found');
    }
    
    const accountData = accountDoc.data();
    const currentPartners = accountData?.mutualAidPartners || [];
    
    // Check if partner already added
    if (currentPartners.includes(partnerAccountId)) {
      throw new Error('Partner already added');
    }
    
    // Verify partner account exists
    const partnerDoc = await firestore()
      .collection('accounts')
      .doc(partnerAccountId)
      .get();
    
    if (!partnerDoc.exists) {
      throw new Error('Partner account not found');
    }
    
    // Add partner to array
    await accountRef.update({
      mutualAidPartners: firestoreModule.FieldValue.arrayUnion(partnerAccountId),
    });
    
    console.log('✅ Mutual aid partner added:', partnerAccountId);
  } catch (error) {
    console.error('Error adding mutual aid partner:', error);
    throw error;
  }
}

/**
 * Remove a mutual aid partner from your account
 */
export async function removeMutualAidPartner(
  yourAccountId: string,
  partnerAccountId: string,
): Promise<void> {
  try {
    const accountRef = firestore().collection('accounts').doc(yourAccountId);
    
    // Remove from both mutualAidPartners and enabledMutualAidPartners
    await accountRef.update({
      mutualAidPartners: firestoreModule.FieldValue.arrayRemove(partnerAccountId),
      enabledMutualAidPartners: firestoreModule.FieldValue.arrayRemove(partnerAccountId),
    });
    
    console.log('✅ Mutual aid partner removed:', partnerAccountId);
  } catch (error) {
    console.error('Error removing mutual aid partner:', error);
    throw error;
  }
}

/**
 * Get partner account details
 */
export async function getPartnerAccount(partnerAccountId: string): Promise<{
  id: string;
  name: string;
  displayName?: string;
  municipality: string;
  civilianMapAccess?: boolean;
  verified?: boolean;
} | null> {
  try {
    const partnerDoc = await firestore()
      .collection('accounts')
      .doc(partnerAccountId)
      .get();
    
    if (!partnerDoc.exists) {
      return null;
    }
    
    const partnerData = partnerDoc.data();
    
    return {
      id: partnerDoc.id,
      name: partnerData?.name || '',
      displayName: partnerData?.displayName,
      municipality: partnerData?.municipality || '',
      civilianMapAccess: partnerData?.civilianMapAccess,
      verified: partnerData?.verified === true,
    };
  } catch (error) {
    console.error('Error getting partner account:', error);
    throw error;
  }
}

/**
 * Toggle enabled state for a mutual aid partner
 * When enabled, the partner's map tab will appear on the map screen
 */
export async function toggleEnabledMutualAidPartner(
  yourAccountId: string,
  partnerAccountId: string,
  enabled: boolean,
): Promise<void> {
  try {
    const accountRef = firestore().collection('accounts').doc(yourAccountId);
    const accountDoc = await accountRef.get();
    
    if (!accountDoc.exists) {
      throw new Error('Account not found');
    }
    
    const accountData = accountDoc.data();
    const currentEnabled = accountData?.enabledMutualAidPartners || [];
    
    if (enabled) {
      // Add to enabled list if not already there
      if (!currentEnabled.includes(partnerAccountId)) {
        await accountRef.update({
          enabledMutualAidPartners: firestoreModule.FieldValue.arrayUnion(partnerAccountId),
        });
      }
    } else {
      // Remove from enabled list
      await accountRef.update({
        enabledMutualAidPartners: firestoreModule.FieldValue.arrayRemove(partnerAccountId),
      });
    }
    
    console.log(`✅ Mutual aid partner ${enabled ? 'enabled' : 'disabled'}:`, partnerAccountId);
  } catch (error) {
    console.error('Error toggling enabled mutual aid partner:', error);
    throw error;
  }
}

/**
 * Toggle notification preference for a mutual aid partner (user-level)
 * Each user can decide if they want to receive notifications from a partner
 */
export async function toggleMutualAidNotificationPreference(
  userId: string,
  partnerAccountId: string,
  enabled: boolean,
): Promise<void> {
  try {
    const userRef = firestore().collection('users').doc(userId);
    
    await userRef.update({
      [`mutualAidNotificationPreferences.${partnerAccountId}`]: enabled,
      updatedAt: firestoreModule.FieldValue.serverTimestamp(),
    });
    
    console.log(`✅ Mutual aid notification preference ${enabled ? 'enabled' : 'disabled'} for partner:`, partnerAccountId);
  } catch (error) {
    console.error('Error toggling mutual aid notification preference:', error);
    throw error;
  }
}

/**
 * Get all users from partner accounts who have enabled notifications for the given source account
 * Used when sending notifications about closures
 */
export async function getPartnerUsersWithNotificationsEnabled(
  sourceAccountId: string,
): Promise<Array<{userId: string; fcmToken?: string}>> {
  try {
    // Find all accounts that have sourceAccountId as a mutual aid partner
    const accountsSnapshot = await firestore()
      .collection('accounts')
      .where('mutualAidPartners', 'array-contains', sourceAccountId)
      .get();
    
    if (accountsSnapshot.empty) {
      return [];
    }
    
    const partnerAccountIds = accountsSnapshot.docs.map(doc => doc.id);
    const usersWithNotifications: Array<{userId: string; fcmToken?: string}> = [];
    
    // For each partner account, find users who have notifications enabled for sourceAccountId
    for (const partnerAccountId of partnerAccountIds) {
      // Get account to find user IDs
      const accountDoc = await firestore().collection('accounts').doc(partnerAccountId).get();
      const accountData = accountDoc.data();
      const userIds = accountData?.userIds || [];
      
      // Check each user's notification preferences
      for (const userId of userIds) {
        const userDoc = await firestore().collection('users').doc(userId).get();
        if (userDoc.exists) {
          const userData = userDoc.data();
          const notificationEnabled = userData?.mutualAidNotificationPreferences?.[sourceAccountId] === true;
          
          if (notificationEnabled && userData?.fcmToken) {
            usersWithNotifications.push({
              userId,
              fcmToken: userData.fcmToken,
            });
          }
        }
      }
    }
    
    console.log(`📤 Found ${usersWithNotifications.length} partner users with notifications enabled for account:`, sourceAccountId);
    return usersWithNotifications;
  } catch (error) {
    console.error('Error getting partner users with notifications enabled:', error);
    return [];
  }
}

